'use client';

import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { pairKindOf } from '@cineamo/api/models/seat/seat-pairs';
import { SeatType } from '@cineamo/api/models/seat/SeatDto.types';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { tailwindColors } from '@cineamo/ui/src/helpers/tailwind-config-helper';

import { IconBeanbag16 } from '@cineamo/ui/src/icons/ic-beanbag';
import { IconBed16 } from '@cineamo/ui/src/icons/ic-bed';
import { IconClose16 } from '@cineamo/ui/src/icons/ic-close';
import { IconHeartSolid16 } from '@cineamo/ui/src/icons/ic-heartSolid';
import { IconWheelchair16 } from '@cineamo/ui/src/icons/ic-wheelchair';
import { DrawerOptions, HEX_COLOR } from '@cineamo/ui/src/seat-map/Drawer';
import { seatColors } from '@cineamo/ui/src/seat-map/exampleData';
import { SeatCategory } from '@cineamo/ui/src/seat-map/typedefs';
import { DBOX_COLOR, LOCKED_SEAT_COLOR } from '@cineamo/ui/src/seat-map-3d/seat-colors';

import { blurUnlessKeyboard } from './focus-modality';

/** The furniture a room has that the map marks with an icon: loveseats, beds, beanbags. */
export type SeatMapLegendFurniture = {
    love?: boolean;
    bed?: boolean;
    beanbag?: boolean;
    dBox?: boolean;
};

export type SeatMapLegendProps = {
    seatCategories: SeatCategory[];
    className?: string;
    renderSeatTypes: SeatMapLegendFurniture & {
        unavailable?: boolean;
        selected?: boolean;
        wheelchair?: boolean;
        table?: boolean;
    };
    wrap?: boolean;
    /**
     * The categories of this room with a showcase become one tappable item, name and surcharge, that opens it
     * (CIN-15400). `surcharge` formats what the category costs extra; absent when it costs no extra.
     */
    showcase?: {
        categoryIds: ReadonlySet<SeatCategory['id']>;
        onOpen: (category: SeatCategory) => void;
        surcharge: (category: SeatCategory) => string | undefined;
    };
};

/** Which furniture icons the seats of a room show, for the legend. */
export function legendFurnitureOf(seats: { type: SeatType; isDbox?: boolean }[]): SeatMapLegendFurniture {
    return {
        love: seats.some((seat) => pairKindOf(seat.type) === 'loveseat'),
        bed: seats.some((seat) => pairKindOf(seat.type) === 'bed'),
        beanbag: seats.some((seat) => seat.type === SeatType.BEANBAG),
        dBox: seats.some((seat) => seat.isDbox === true)
    };
}

const SeatMapLegend: React.FC<SeatMapLegendProps> = ({
    seatCategories,
    className,
    wrap = false,
    renderSeatTypes,
    showcase
}) => {
    const t = useTranslations('tickets');
    const seatColorsByCategory = useMemo<DrawerOptions['seatColorsByCategory']>(() => {
        const obj: Record<string, HEX_COLOR> = {};
        if (seatCategories) {
            seatCategories.forEach((category, index) => {
                obj[category.name ?? ''] = (category.color ?? seatColors[index % seatColors.length]) as HEX_COLOR;
            });
        }
        return obj;
    }, [seatCategories]);

    // The fade and its room on the right say "more to scroll": only true when the last item reaches past the edge.
    const listRef = useRef<HTMLUListElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    useEffect(() => {
        const list = listRef.current;
        if (!list) return;
        const measure = () => {
            const last = list.lastElementChild as HTMLElement | null;
            setIsOverflowing(!!last && last.offsetLeft + last.offsetWidth > list.clientWidth);
        };
        const observer = new ResizeObserver(measure);
        observer.observe(list);
        // The items change with the room and the prices, not only with the strip's own size.
        Array.from(list.children).forEach((item) => observer.observe(item));
        return () => observer.disconnect();
    }, [seatCategories, renderSeatTypes, showcase]);

    return (
        <div className={cn('w-full flex flex-col', isOverflowing && 'gradient-mask-r-90', className)}>
            <ul
                ref={listRef}
                className={cn(
                    'relative flex overflow-x-auto gap-x-4 gap-y-8 lg:gap-x-16 lg:mx-auto no-scrollbar py-[4px] lg:py-0',
                    isOverflowing && 'pr-24',
                    wrap ? 'flex-wrap items-start content-start row-gap-8' : 'flex-nowrap'
                )}>
                {seatCategories.map((category) => {
                    const swatch = (
                        <div
                            className="size-[20px] shrink-0 rounded-4"
                            style={{ background: seatColorsByCategory[category.name] }}
                        />
                    );
                    const price = showcase?.surcharge(category);
                    return (
                        <li
                            className="flex flex-row items-center"
                            key={category.id}>
                            {showcase?.categoryIds.has(category.id) ? (
                                // One target of 44px: name and surcharge tell the guest what a seat costs, the tap shows why.
                                <button
                                    type="button"
                                    // A tap must not focus the pill: the browser would scroll the strip to reveal it.
                                    onMouseDown={(event) => event.preventDefault()}
                                    onFocus={blurUnlessKeyboard}
                                    onClick={() => showcase.onOpen(category)}
                                    aria-label={t('seat-category-info', { name: category.name })}
                                    className="flex min-h-[44px] cursor-pointer items-center gap-6 lg:gap-8 rounded-16 bg-[rgba(var(--on-background-color-rgb),0.1)] px-8 lg:px-12 hover:bg-[rgba(var(--on-background-color-rgb),0.2)] text-body-bold-12 leading-none tracking-wider whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-[var(--on-background-color)]">
                                    {swatch}
                                    {category.name}
                                    {price && <span>+{price}</span>}
                                    <span
                                        aria-hidden="true"
                                        className="opacity-70">
                                        &rsaquo;
                                    </span>
                                </button>
                            ) : (
                                <span className="flex flex-row items-center gap-8 text-body-bold-12 leading-none tracking-wider">
                                    {swatch}
                                    {category.name}
                                </span>
                            )}
                        </li>
                    );
                })}
                {renderSeatTypes.unavailable && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className="size-[20px] flex items-center justify-center rounded-4"
                            style={{ background: LOCKED_SEAT_COLOR }}>
                            <IconClose16
                                className="w-[80%] h-auto"
                                fill="white"
                            />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('unavailable')}</span>
                    </li>
                )}
                {renderSeatTypes.selected && (
                    <li className="flex flex-row items-center space-x-8">
                        <div className="size-[20px] flex items-center justify-center rounded-4 bg-[var(--primary-color)]">
                            <div className="w-[50%] h-auto aspect-[1] bg-black rounded-full" />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('selected')}</span>
                    </li>
                )}
                {renderSeatTypes.wheelchair && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className="size-[20px] flex items-center justify-center rounded-4"
                            style={{ background: seatCategories[0]?.color }}>
                            <IconWheelchair16
                                className="w-[80%] h-auto opacity-50"
                                fill={tailwindColors.darkBlue.DEFAULT}
                            />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('wheelchair')}</span>
                    </li>
                )}
                {renderSeatTypes.love && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className="size-[20px] flex items-center justify-center rounded-4"
                            style={{ background: seatCategories[0]?.color }}>
                            <IconHeartSolid16
                                className="w-[80%] h-auto opacity-50"
                                fill={tailwindColors.darkBlue.DEFAULT}
                            />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('love-seat')}</span>
                    </li>
                )}
                {renderSeatTypes.bed && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className="size-[20px] flex items-center justify-center rounded-4"
                            style={{ background: seatCategories[0]?.color }}>
                            <IconBed16
                                className="w-[80%] h-auto opacity-50"
                                fill={tailwindColors.darkBlue.DEFAULT}
                            />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('bed')}</span>
                    </li>
                )}
                {renderSeatTypes.beanbag && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className="size-[20px] flex items-center justify-center rounded-4"
                            style={{ background: seatCategories[0]?.color }}>
                            <IconBeanbag16
                                className="w-[80%] h-auto opacity-50"
                                fill={tailwindColors.darkBlue.DEFAULT}
                            />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('beanbag')}</span>
                    </li>
                )}
                {renderSeatTypes.dBox && (
                    <li className="flex flex-row items-center space-x-8">
                        {/* The strip the 3D map draws on the front foot of a D-BOX seat (DboxMarkers). */}
                        <div
                            className="size-[20px] flex items-end justify-center rounded-4 pb-[3px]"
                            style={{ background: seatCategories[0]?.color }}>
                            <div
                                className="h-[3px] w-[70%] rounded-[1px]"
                                style={{ background: DBOX_COLOR }}
                            />
                        </div>
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('d-box')}</span>
                    </li>
                )}
                {renderSeatTypes.table && (
                    <li className="flex flex-row items-center space-x-8">
                        <div
                            className={cn(
                                'size-[20px] flex items-center justify-center rounded-4',
                                'bg-[var(--on-background-color)]'
                            )}
                        />
                        <span className="flex-1 text-body-bold-12 leading-none tracking-wider">{t('table')}</span>
                    </li>
                )}
            </ul>
        </div>
    );
};

export default SeatMapLegend;
